/**
 * Ensure an internal URL path ends with a trailing slash.
 *
 * Preserves query strings and hash fragments, inserting the slash
 * before them. File-like paths (last segment contains a dot, e.g.
 * `/document.pdf`) are left unchanged.
 *
 * Non-path values (empty string, hash-only, `mailto:`, `tel:`) pass
 * through unmodified.
 *
 * Mirrors the proxy-level enforcement in `proxy.ts` (SEO spec §12)
 * so that CMS-authored links never trigger a 301 redirect.
 */
export function ensureTrailingSlash(url: string): string {
  if (
    !url ||
    url.startsWith('#') ||
    url.startsWith('?') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:')
  ) {
    return url;
  }

  const queryIndex = url.indexOf('?');
  const hashIndex = url.indexOf('#');

  let pathnameEnd = url.length;
  if (queryIndex !== -1) pathnameEnd = queryIndex;
  if (hashIndex !== -1 && hashIndex < pathnameEnd) pathnameEnd = hashIndex;

  const pathname = url.slice(0, pathnameEnd);
  const suffix = url.slice(pathnameEnd);

  const lastSegment = pathname.split('/').pop() || '';
  if (lastSegment.includes('.')) return url;

  if (pathname.endsWith('/')) return url;
  return `${pathname}/${suffix}`;
}
